import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export interface OutboundIntegration {
  id: string;
  name: string;
  destination_url: string;
  vault_secret_name: string;
  key_prefix: string | null;
  enabled: boolean;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface OutboundEvent {
  id: string;
  integration_id: string;
  occurred_at: string;
  event_type: string;
  http_status: number | null;
  ok: boolean;
  duration_ms: number | null;
  request_id: number | null;
  error: string | null;
}

const INTEGRATION_NAME = "launchpanda";
const VAULT_SECRET_NAME = "outbound_launchpanda_api_key";
const TIMEOUT_MS = 10_000;

// Loaded inside handlers only — server-only module must not enter the client graph.
async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function assertAdmin(userId: string) {
  const db = await admin();
  const { data, error } = await db.from("user_roles").select("role").eq("user_id", userId);
  if (error) throw new Error(error.message);
  if (!(data ?? []).some((r) => r.role === "admin")) throw new Error("Admin only");
  return db;
}

function maskPrefix(key: string) {
  const head = key.slice(0, 12);
  return `${head}${"•".repeat(8)}`;
}

/** Reads (and lazily creates) the single LaunchPanda integration row. Never returns key material. */
export const getOutboundIntegration = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const db = await assertAdmin(context.userId);
    const { data, error } = await db
      .from("outbound_integrations")
      .select("*")
      .eq("name", INTEGRATION_NAME)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data ?? null) as OutboundIntegration | null;
  });

export const listOutboundEvents = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z
      .object({
        from: z.string().optional().nullable(),
        to: z.string().optional().nullable(),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ context, data }) => {
    const db = await assertAdmin(context.userId);
    let q = db
      .from("outbound_events")
      .select("id, integration_id, occurred_at, event_type, http_status, ok, duration_ms, request_id, error")
      .order("occurred_at", { ascending: false })
      .limit(50);
    if (data.from) q = q.gte("occurred_at", `${data.from}T00:00:00Z`);
    if (data.to) q = q.lte("occurred_at", `${data.to}T23:59:59Z`);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return (rows ?? []) as unknown as OutboundEvent[];
  });

export const saveOutboundIntegration = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z
      .object({
        destination_url: z.string().trim().url("Must be a valid URL"),
        enabled: z.boolean(),
        api_key: z.string().trim().min(8).max(500).optional().or(z.literal("")),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    const db = await assertAdmin(context.userId);
    const url = data.destination_url.replace(/\/+$/, "");

    const patch: Record<string, unknown> = {
      name: INTEGRATION_NAME,
      destination_url: url,
      vault_secret_name: VAULT_SECRET_NAME,
      enabled: data.enabled,
    };

    if (data.api_key) {
      const { error: vErr } = await db.rpc("set_outbound_secret", {
        _name: VAULT_SECRET_NAME,
        _value: data.api_key,
      } as never);
      if (vErr) throw new Error(vErr.message);
      patch.key_prefix = maskPrefix(data.api_key);
    }

    const { data: row, error } = await db
      .from("outbound_integrations")
      .upsert(patch as never, { onConflict: "name" })
      .select("*")
      .single();
    if (error) throw new Error(error.message);
    return row as OutboundIntegration;
  });

export const testOutboundConnection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const db = await assertAdmin(context.userId);

    const { data: cfg, error: cfgErr } = await db
      .from("outbound_integrations")
      .select("*")
      .eq("name", INTEGRATION_NAME)
      .maybeSingle();
    if (cfgErr) throw new Error(cfgErr.message);
    if (!cfg) throw new Error("Not configured — save a destination URL and API key first.");

    const config = cfg as OutboundIntegration;

    const { data: key, error: keyErr } = await db.rpc("get_outbound_secret", {
      _name: config.vault_secret_name,
    } as never);
    if (keyErr) throw new Error(keyErr.message);
    if (!key) throw new Error("No API key stored — save one first.");

    const endpoint = `${config.destination_url.replace(/\/+$/, "")}/api/public/v1/ingest/ping`;
    const started = Date.now();

    let status: number | null = null;
    let ok = false;
    let bodyText = "";
    let errText: string | null = null;

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${key as string}`,
          "Content-Type": "application/json",
        },
        body: "{}",
        signal: AbortSignal.timeout(TIMEOUT_MS),
      });
      status = res.status;
      ok = res.ok;
      bodyText = (await res.text()).slice(0, 2000);
      if (!ok) errText = `HTTP ${status}: ${bodyText.slice(0, 500)}`;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      errText =
        e instanceof Error && (e.name === "TimeoutError" || e.name === "AbortError")
          ? `Timed out after ${TIMEOUT_MS} ms`
          : msg;
    }

    const duration = Date.now() - started;

    await db.from("outbound_events").insert({
      integration_id: config.id,
      event_type: "test",
      http_status: status,
      ok,
      duration_ms: duration,
      error: errText,
      payload_summary: { endpoint, triggered_by: context.userId },
    } as never);

    await db
      .from("outbound_integrations")
      .update(
        (ok
          ? { last_success_at: new Date().toISOString(), last_error: null }
          : { last_failure_at: new Date().toISOString(), last_error: errText }) as never,
      )
      .eq("id", config.id);

    return { ok, status, duration_ms: duration, body: bodyText, error: errText };
  });
